import { cn } from '@/app/lib/utils';
import * as React from 'react';

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: React.ReactNode;
  showAsterisk?: boolean;
  wrapperClassName?: string;
  characterLimit?: number;
};

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { className, label, showAsterisk = false, id, wrapperClassName, characterLimit, ...props },
    ref
  ) => {
    const inputId = React.useId();
    const finalId = id || inputId;
    const [value, setValue] = React.useState<string>((props.value as string) || '');

    React.useEffect(() => {
      setValue(props.value as string);
    }, [props.value]);

    return (
      <>
        {label && (
          <label htmlFor={finalId} className="text-sm font-medium text-foreground mb-1.5 flex">
            {label}
            {showAsterisk && <span className="text-red-600">&nbsp;*</span>}
          </label>
        )}
        <div
          className={cn(
            'input-group flex items-center overflow-hidden relative rounded-md border border-input bg-white dark:bg-input/30 ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 has-disabled:cursor-not-allowed has-disabled:opacity-50',
            wrapperClassName
          )}
        >
          <textarea
            id={finalId}
            className={cn(
              'flex min-h-20 w-full resize-none bg-transparent py-2 px-3 file:border-0 file:bg-transparent font-normal file:text-sm file:font-medium text-sm text-foreground placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:bg-transparent focus:outline-0',
              'outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
              'pl-[14px]',
              className
            )}
            ref={ref}
            {...props}
            value={value}
            onPaste={e => {
              e.preventDefault();

              const el = e.target as HTMLTextAreaElement;

              // check it's selected all
              const start = el.selectionStart;
              const end = el.selectionEnd;
              const isSelected = start !== end;

              const pastedData = e.clipboardData.getData('text');

              let newText = value || '';

              if (isSelected) {
                // replace selected text
                newText = newText.slice(0, start) + pastedData + newText.slice(end);
              } else {
                newText = newText.slice(0, start) + pastedData + newText.slice(start);
              }

              if (characterLimit && newText.length > characterLimit) {
                // remove the last character
                newText = newText.slice(0, characterLimit);
              }
              setValue(newText);
              props.onChange?.({
                target: { value: newText },
              } as React.ChangeEvent<HTMLTextAreaElement>);
            }}
            onChange={e => {
              let newText = e.target.value;
              if (characterLimit && e.target.value.length > characterLimit) {
                newText = e.target.value.slice(0, characterLimit);
              }
              setValue(newText);
              props.onChange?.({
                ...e,
                target: { ...e.target, value: newText },
              } as React.ChangeEvent<HTMLTextAreaElement>);
            }}
            autoComplete="off"
          />
          {characterLimit ? (
            <p className="pl-1 text-xs text-muted-foreground absolute right-2 bottom-1 bg-background pointer-events-none">
              {value?.length || 0}/{characterLimit}
            </p>
          ) : null}
        </div>
      </>
    );
  }
);

Textarea.displayName = 'Textarea';
export default Textarea;
